import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CheckboxInputField,
  ControlledField,
  FileTypeEnum,
  InlineFileInputField,
  RadioControlGroup,
  RadioControlGroupOptions,
  RadioOption,
  RideDtoSchemaType,
  SelectInputField,
  SpecialLuggageType,
  TextareaInputField,
  colors,
} from 'remote/global';
import { Box } from '@mui/system';
import { PersonOutline } from '@mui/icons-material';
import Cookies from 'js-cookie';
import { CookieKeyEnum } from 'utils/data/enums';
import { toast } from 'react-hot-toast';
import {
  useOperatorsOrdersQuery,
  usePatchOperatorsOrdersDetailsMutation,
} from 'api/queries/operators';
import { useDeleteOrdersFilesMutation, usePostOrdersFilesMutation } from 'api/queries/orders';
import { differenceWith, isEqual } from 'lodash-es';
import { runSequentially } from 'utils/helpers';
import {
  BookingExtrasSchemaType,
  OrderDetailsSchemaPayloadType,
  bookingExtrasSchema,
} from './types';

const specialLuggageOptions = Object.values(SpecialLuggageType);

function BookingEditExtrasForm({
  defaultValues,
  bookingNumber,
  isOrderEditAvailable,
}: {
  defaultValues?: BookingExtrasSchemaType;
  bookingNumber: string;
  ride: RideDtoSchemaType;
  isOrderEditAvailable: boolean;
}) {
  const tInstance = useTranslation();
  const { data: selectedOrder } = useOperatorsOrdersQuery(bookingNumber);
  const { t } = tInstance;
  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<BookingExtrasSchemaType>({
    defaultValues: {
      internalInfo: '',
      passengerCount: '' as any,
      pickUpLocationMessage: '',
      rideDetailsFiles: undefined,
      rideDetailsFileUrl: '',
      signFiles: undefined,
      signFileUrl: '',
      signText: '',
      specialLuggage:
        defaultValues?.specialLuggageCount != null && defaultValues.specialLuggageCount !== 0,
      specialLuggageCount: null,
      specialLuggageType: null,
      overnight: false,
      ...defaultValues,
    },
    resolver: zodResolver(bookingExtrasSchema),
  });
  const patchOrdersDetails = usePatchOperatorsOrdersDetailsMutation(bookingNumber);
  const deleteOrdersFiles = useDeleteOrdersFilesMutation(bookingNumber);
  const postOrdersFiles = usePostOrdersFilesMutation(bookingNumber);
  const isSpecialLuggage = watch('specialLuggage') === true;
  const isOvernight = watch('overnight') === true;

  const jwtToken = Cookies.get(CookieKeyEnum.JWT_TOKEN) as string;

  const onValid = async (data: BookingExtrasSchemaType) => {
    const { signFiles, rideDetailsFiles, ...rest } = data;
    const payload: OrderDetailsSchemaPayloadType = {
      ...selectedOrder!,
      ...rest,
      groupLead: selectedOrder?.groupLead,
    } as any;

    try {
      await patchOrdersDetails.mutateAsync(payload);

      const defaultFiles = [
        ...(defaultValues?.signFiles ?? []),
        ...(defaultValues?.rideDetailsFiles ?? []),
      ];
      const files = [...(signFiles ?? []), ...(rideDetailsFiles ?? [])];
      // A file is uploaded if there is 'originalFile' attached
      const uploadedFiles = files.filter((f) => f.originalFile != null);
      const removedFiles = differenceWith(defaultFiles, files, isEqual);

      const removedPromises = removedFiles.map(
        ({ fileName }) =>
          () =>
            deleteOrdersFiles.mutateAsync({ fileName })
      );
      const insertedPromises = uploadedFiles.map((file) => () => {
        const formData = new FormData();
        formData.append('file', file.originalFile as File);
        formData.append('fileType', file.fileType);
        return postOrdersFiles.mutateAsync(formData);
      });

      // Call the promise functions sequentially
      await runSequentially(removedPromises);
      await runSequentially(insertedPromises);
    } catch (err) {
      toast.error(t('Toast.failure'));
    }
  };

  const handleRejectFile = (alertMessage: string) => toast.error(alertMessage);

  const driverAccommodationTypeOptions: RadioOption[] = [
    {
      label: t('FormFields.driverAccommodationType.options[0].label'),
      value: t('FormFields.driverAccommodationType.options[0].value').toString(),
      helperText: t('FormFields.driverAccommodationType.options[0].helperText').toString(),
    },
    {
      label: t('FormFields.driverAccommodationType.options[1].label'),
      value: t('FormFields.driverAccommodationType.options[1].value').toString(),
      helperText: t('FormFields.driverAccommodationType.options[1].helperText').toString(),
    },
  ];

  return (
    <Box sx={{ maxWidth: '350px', mx: 'auto', my: '2rem' }}>
      <form onSubmit={handleSubmit(onValid)}>
        <Grid container gap={2}>
          <Grid item xs={12}>
            <Typography
              sx={{ color: colors.uiBlue600, mb: '12px', fontWeight: 600, fontSize: '18px' }}
            >
              {t('terms.attributes.vehicle.extras')}
            </Typography>
            {isOvernight && (
              <ControlledField
                control={control}
                name='driverAccommodationType'
                render={(renderProps) => (
                  <RadioControlGroup renderProps={renderProps} disabled={!isOrderEditAvailable}>
                    <RadioControlGroupOptions
                      options={driverAccommodationTypeOptions}
                      renderProps={renderProps}
                    />
                  </RadioControlGroup>
                )}
              />
            )}
            <ControlledField
              control={control}
              name='passengerCount'
              fieldProps={{
                disabled: !isOrderEditAvailable,
                helperText: '',
                InputProps: {
                  endAdornment: (
                    <Box sx={{ userSelect: 'none', pointerEvents: 'none', lineHeight: '16px' }}>
                      <PersonOutline sx={{ color: colors.gray3 }} />
                    </Box>
                  ),
                },
              }}
            />
            <ControlledField
              control={control}
              name='internalInfo'
              render={(renderProps) => (
                <TextareaInputField
                  {...renderProps}
                  field={{
                    ...renderProps.field,
                    disabled: !isOrderEditAvailable,
                  }}
                />
              )}
            />
            <ControlledField
              control={control}
              name='pickUpLocationMessage'
              render={(renderProps) => (
                <TextareaInputField
                  {...renderProps}
                  field={{
                    ...renderProps.field,
                    disabled: !isOrderEditAvailable,
                  }}
                />
              )}
            />
            <ControlledField
              control={control}
              disabled={!isOrderEditAvailable}
              name='rideDetailsFiles'
              render={(renderProps) => (
                <InlineFileInputField
                  type={FileTypeEnum.RIDE_DETAILS}
                  {...renderProps}
                  jwtToken={jwtToken}
                  onFileRejected={handleRejectFile}
                />
              )}
            />
            <ControlledField
              control={control}
              name='signText'
              fieldProps={{
                disabled: !isOrderEditAvailable,
              }}
            />
            <ControlledField
              control={control}
              disabled={!isOrderEditAvailable}
              name='signFiles'
              render={(renderProps) => (
                <InlineFileInputField
                  type={FileTypeEnum.RIDE_SIGN}
                  {...renderProps}
                  jwtToken={jwtToken}
                  onFileRejected={handleRejectFile}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography
              sx={{ color: colors.uiBlue600, mb: '12px', fontWeight: 600, fontSize: '18px' }}
            >
              {t('terms.attributes.specialLuggage')}
            </Typography>
            <ControlledField
              control={control}
              name='specialLuggage'
              render={(renderProps) => (
                <CheckboxInputField
                  fieldProps={{
                    disabled: !isOrderEditAvailable,
                  }}
                  {...renderProps}
                />
              )}
            />

            {isSpecialLuggage && (
              <Grid container item spacing={1}>
                <Grid item xs={6}>
                  <ControlledField control={control} name='specialLuggageCount' />
                </Grid>
                <Grid item xs={6}>
                  <ControlledField
                    control={control}
                    name='specialLuggageType'
                    defaultValue=''
                    render={(renderProps) => (
                      <SelectInputField
                        {...renderProps}
                        options={specialLuggageOptions.map((value) => ({
                          label: t(`SpecialLuggageType.${value}`).toString(),
                          value,
                        }))}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Button
          type='submit'
          disabled={!isOrderEditAvailable || isSubmitting || isValidating || isLoading}
          variant='contained'
          fullWidth
          sx={{ mt: '2rem' }}
        >
          {t('save')}
        </Button>
      </form>
    </Box>
  );
}

export default BookingEditExtrasForm;
